/**
 * The `Tableordion` class manages accordion UI components within a table structure.
 * It handles the initialization of accordion rows, alternating row styles, and the
 * animation of content expansion and collapse upon user interaction.
 */
class Tableordion {
    private accordionRows: NodeListOf<HTMLElement>;

    /**
     * Creates an instance of `Tableordion`.
     *
     * @param accordionSelector - A CSS selector string targeting the accordion row elements.
     */
    constructor(accordionSelector: string) {
        const rows = document.querySelectorAll<HTMLElement>(accordionSelector);
        if (rows.length === 0) {
            console.warn(`No accordion rows found with selector: ${accordionSelector}`);
        }
        this.accordionRows = rows;
        this.initializeRows();
        this.addEventListeners();
    }

    /**
     * Initializes accordion rows by setting default states and applying alternating styles.
     */
    private initializeRows(): void {
        this.accordionRows.forEach((button, index) => {
            const collapseToggle = button.querySelector<HTMLElement>('.collapse-toggle');
            if (collapseToggle) {
                collapseToggle.setAttribute('data-toggle', 'expand'); // Default state is collapsed
            }
            if (index % 2 === 0) {
                button.classList.add('accordion-odd'); // Odd rows
            } else {
                button.classList.add('accordion-even'); // Even rows
            }
        });
    }

    /**
     * Animates the expansion or collapse of the accordion content.
     *
     * @param contentRow - The table row element containing the accordion content.
     * @param isExpanding - A boolean indicating whether to expand (`true`) or collapse (`false`) the content.
     */
    private animateAccordion(contentRow: HTMLElement, isExpanding: boolean): void {
        const innerContent = contentRow.querySelector<HTMLElement>('.accordion-inner-content');

        if (!innerContent) {
            console.error('Accordion inner content not found.');
            return;
        }

        if (isExpanding) {
            contentRow.style.display = 'table-row';
            innerContent.style.height = '0';
            innerContent.style.opacity = '0';

            const scrollHeight = `${innerContent.scrollHeight}px`;

            setTimeout(() => {
                innerContent.style.height = scrollHeight;
                innerContent.style.opacity = '1';
            }, 10);

            const expandHandler = (event: TransitionEvent) => {
                if (event.propertyName === 'height') {
                    innerContent.style.height = 'auto';
                    innerContent.removeEventListener('transitionend', expandHandler);
                }
            };

            innerContent.addEventListener('transitionend', expandHandler);
        } else {
            innerContent.style.height = `${innerContent.scrollHeight}px`;
            innerContent.style.opacity = '1';

            setTimeout(() => {
                innerContent.style.height = '0';
                innerContent.style.opacity = '0';
            }, 10);

            const collapseHandler = (event: TransitionEvent) => {
                if (event.propertyName === 'height') {
                    contentRow.style.display = 'none';
                    innerContent.removeEventListener('transitionend', collapseHandler);
                }
            };

            innerContent.addEventListener('transitionend', collapseHandler);
        }
    }

    /**
     * Adds click event listeners to each accordion row to handle user interactions.
     */
    private addEventListeners(): void {
        this.accordionRows.forEach((button) => {
            button.addEventListener('click', () => {
                const contentRow = button.nextElementSibling as HTMLElement | null;
                const collapseToggle = button.querySelector<HTMLElement>('.collapse-toggle');

                if (!contentRow) {
                    console.error('Content row not found for the clicked accordion button.');
                    return;
                }

                if (!collapseToggle) {
                    console.warn('Collapse toggle element not found within the accordion button. This could be intentional or a mistake. Please check the HTML structure.');
                    return;
                }

                const toggleState = collapseToggle.getAttribute('data-toggle');

                if (toggleState === 'expand') {
                    contentRow.classList.add('accordion-content-active');
                    this.animateAccordion(contentRow, true);
                    collapseToggle.innerHTML = '<i class="fa-duotone fa-solid fa-square-chevron-up" style="color: #F6961D;"></i>';
                    collapseToggle.setAttribute('data-toggle', 'collapse');
                } else {
                    this.animateAccordion(contentRow, false);
                    contentRow.classList.remove('accordion-content-active');
                    collapseToggle.innerHTML = '<i class="fa-duotone fa-solid fa-square-chevron-down" style="color: #585555"></i>';
                    collapseToggle.setAttribute('data-toggle', 'expand');
                }
            });
        });
    }
}

// Exporting the class to make it reusable
export default Tableordion;